import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clustersApi } from '../../../api/client';
import Modal, { Button, Badge } from '../../ui/Modal';
import { Label, Input, ErrorBanner } from '../../../auth/AuthShell';

/**
 * Phase H — Cluster Management & Failover (2026-06-19)
 *
 * Shows live state of all ACD clusters (DEN, LOU), runs health probes
 * (AMI, SIP/dispatcher, Postgres replication lag), and lets admins
 * promote a standby cluster to active with an audit reason.
 *
 * The failover backend does the actual flip inside a single transaction;
 * all downstream services (`writable_ami_targets`, recording_worker,
 * wallboard) pick up the new active cluster on their next call.
 */

function tone(status) {
  if (status === 'ok')    return 'green';
  if (status === 'warn')  return 'amber';
  if (status === 'error') return 'red';
  return 'neutral';
}

function StateBadge({ state }) {
  if (state === 'active')   return <Badge tone="green">ACTIVE</Badge>;
  if (state === 'standby')  return <Badge tone="amber">standby</Badge>;
  if (state === 'disabled') return <Badge tone="red">disabled</Badge>;
  return <Badge>{state}</Badge>;
}

function ProbeRow({ label, probe }) {
  if (!probe) {
    return (
      <div className="flex items-center gap-2 py-1 text-sm">
        <span className="w-24 text-surface-muted">{label}:</span>
        <Badge>n/a</Badge>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 py-1 text-sm">
      <span className="w-24 text-surface-muted">{label}:</span>
      <Badge tone={tone(probe.status)}>{probe.status}</Badge>
      <span className="text-surface-muted truncate flex-1" title={probe.detail}>
        {probe.detail}
      </span>
      {typeof probe.elapsed_ms === 'number' && probe.elapsed_ms > 0 && (
        <span className="text-xs text-surface-muted whitespace-nowrap">
          {probe.elapsed_ms} ms
        </span>
      )}
    </div>
  );
}

function ClusterCard({ cluster, healthEntry, onPromote }) {
  const isActive = cluster.state === 'active';
  const probes = healthEntry?.probes || {};

  return (
    <div className="rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-panel dark:bg-surface-dark-panel p-5 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold">{cluster.display_name}</h3>
            <StateBadge state={cluster.state} />
          </div>
          <div className="text-xs text-surface-muted mt-1">
            <span className="font-mono">{cluster.name}</span>
            {' · '}
            <span className="font-mono">{cluster.host_ip}</span>
            {' · priority '}
            <span className="font-mono">{cluster.priority}</span>
          </div>
        </div>
        {!isActive && cluster.state !== 'disabled' && (
          <Button variant="primary" onClick={() => onPromote(cluster)}>
            Promote to active
          </Button>
        )}
        {isActive && (
          <Badge tone="green">primary write target</Badge>
        )}
      </div>

      <div className="border-t border-surface-border dark:border-surface-dark-border pt-2">
        <ProbeRow label="AMI" probe={probes.ami} />
        <ProbeRow label="SIP/TCP" probe={probes.sip} />
      </div>
    </div>
  );
}

function PostgresCard({ pg }) {
  if (!pg) return null;
  const role = pg.role || (pg.detail?.startsWith('replica') ? 'replica' : 'primary');
  return (
    <div className="rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-panel dark:bg-surface-dark-panel p-5">
      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-base font-semibold">Postgres replication</h3>
        <Badge tone={role === 'primary' ? 'green' : 'amber'}>{role}</Badge>
        <Badge tone={tone(pg.status)}>{pg.status}</Badge>
      </div>
      <div className="text-sm text-surface-muted">{pg.detail}</div>
      {pg.standbys && pg.standbys.length > 0 && (
        <div className="mt-2 space-y-1 text-xs">
          {pg.standbys.map((s, i) => (
            <div key={i} className="flex gap-3 font-mono">
              <span className="text-surface-muted">{s.addr}</span>
              <span>{s.state}</span>
              <span>{s.sync_state}</span>
              <span>{s.bytes_behind} bytes behind</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PromoteDialog({ open, target, onClose, onConfirm, isPending, error }) {
  const [reason, setReason]   = useState('');
  const [force,  setForce]    = useState(false);

  React.useEffect(() => {
    if (open) {
      setReason('');
      setForce(false);
    }
  }, [open]);

  if (!target) return null;

  return (
    <Modal
      open={open}
      onClose={isPending ? undefined : onClose}
      title={`Promote ${target.display_name} to active`}
      size="md"
      footer={
        <>
          <Button variant="ghost"  onClick={onClose} disabled={isPending}>Cancel</Button>
          <Button
            variant={force ? 'danger' : 'primary'}
            onClick={() => onConfirm(target.name, reason, force)}
            loading={isPending}
            disabled={!reason.trim() || reason.trim().length < 3}
          >
            {force ? 'Force promote' : 'Promote'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm">
          This will atomically:
        </p>
        <ul className="text-sm list-disc list-inside space-y-1 text-surface-muted">
          <li>Demote any currently-active cluster to <code>standby</code></li>
          <li>Promote <code className="font-mono">{target.name}</code> ({target.host_ip}) to <code>active</code></li>
          <li>All CX writes (queue/agent/profile sync, wallboard, recording) start preferring {target.display_name} on the next service call</li>
          <li>No service restarts. AMI sessions stay connected to both Asterisks (dual-subscribe).</li>
        </ul>
        <div>
          <Label htmlFor="reason">Reason (required, will be audited)</Label>
          <Input
            id="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Planned LOU maintenance window 22:00–02:00 MDT"
            disabled={isPending}
          />
          <p className="text-xs text-surface-muted mt-1">
            Min 3 characters. Logged to audit trail with your email + IP.
          </p>
        </div>
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={force}
            onChange={(e) => setForce(e.target.checked)}
            disabled={isPending}
            className="mt-1"
          />
          <span>
            <strong className="text-accent-red">Force (emergency mode):</strong>{' '}
            skip pre-flight health checks. Use only when target cluster is
            verifiably healthy but probes failed for unrelated reasons
            (e.g. network blip).
          </span>
        </label>
        {error && <ErrorBanner>{error}</ErrorBanner>}
      </div>
    </Modal>
  );
}

export default function ClustersPage() {
  const qc = useQueryClient();
  const [promoteTarget, setPromoteTarget] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  const { data: clusters = [], isLoading: clusterLoad } = useQuery({
    queryKey: ['clusters'],
    queryFn:  clustersApi.list,
  });

  const { data: health, isFetching: healthFetch, refetch: refetchHealth } = useQuery({
    queryKey: ['cluster-health'],
    queryFn:  clustersApi.health,
    refetchInterval: 20_000,  // auto-refresh every 20 s
  });

  const failoverMut = useMutation({
    mutationFn: ({ target, reason, force }) =>
      clustersApi.failover(target, reason, force),
    onSuccess: (data) => {
      setLastResult({ ok: true, ...data });
      setPromoteTarget(null);
      qc.invalidateQueries({ queryKey: ['clusters'] });
      qc.invalidateQueries({ queryKey: ['cluster-health'] });
    },
    onError: (e) => {
      // ApiError sets .detail to a readable string
      setLastResult({ ok: false, detail: e.detail || e.message });
    },
  });

  const healthByName = Object.fromEntries(
    (health?.clusters || []).map((c) => [c.name, c])
  );

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold">Clusters</h1>
          <p className="text-sm text-surface-muted dark:text-surface-dark-muted">
            Active/standby ACD cluster control. Promoting a standby is atomic and
            does not require any service restart — downstream services pick up
            the new active cluster on their next call (within seconds for AMI
            writes; up to ~5 min for the next sync cycle).
          </p>
        </div>
        <Button variant="default" onClick={() => refetchHealth()}>
          {healthFetch ? '… refreshing' : '↻ Refresh health'}
        </Button>
      </div>

      {lastResult && (
        <div
          className={
            'rounded-thin p-3 text-sm border ' +
            (lastResult.ok
              ? 'border-green-300 bg-green-50 text-green-900 dark:bg-green-900/30 dark:text-green-100 dark:border-green-700'
              : 'border-red-300 bg-red-50 text-red-900 dark:bg-red-900/30 dark:text-red-100 dark:border-red-700')
          }
        >
          {lastResult.ok ? (
            <>
              ✅ Failover OK. Active cluster is now{' '}
              <code className="font-mono font-semibold">{lastResult.to_active}</code>
              {lastResult.from_active && (
                <> (was <code className="font-mono">{lastResult.from_active}</code>)</>
              )}
              . Reason: <em>{lastResult.reason}</em>
              {lastResult.forced && ' · ⚠️ forced'}
            </>
          ) : (
            <>❌ Failover failed: {lastResult.detail}</>
          )}
          <button
            onClick={() => setLastResult(null)}
            className="ml-3 text-xs underline"
          >dismiss</button>
        </div>
      )}

      {clusterLoad && (
        <div className="text-surface-muted text-sm">Loading clusters…</div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {clusters.map((c) => (
          <ClusterCard
            key={c.id}
            cluster={c}
            healthEntry={healthByName[c.name]}
            onPromote={(t) => setPromoteTarget(t)}
          />
        ))}
      </div>

      <PostgresCard pg={health?.postgres} />

      <PromoteDialog
        open={!!promoteTarget}
        target={promoteTarget}
        onClose={() => setPromoteTarget(null)}
        onConfirm={(target, reason, force) =>
          failoverMut.mutate({ target, reason, force })
        }
        isPending={failoverMut.isPending}
        error={
          failoverMut.isError ? (failoverMut.error?.detail || failoverMut.error?.message) : null
        }
      />
    </div>
  );
}
